/**
 * สั่งปล่อยใบสั่งผลิต — `POST /v1/production-orders/{id}/release`
 *
 * **นี่คือคำสั่งที่ผู้ใช้ต้องเข้าใจก่อนกด ไม่ใช่ปุ่มยืนยันธรรมดา** สิ่งที่เกิดขึ้นใน
 * ธุรกรรมเดียวคือ: หา BOM และ Routing ที่ใช้อยู่จริงของสินค้ากับโรงงานคู่นี้ แล้ว
 * **บันทึกติดไปกับใบนี้ถาวร** พร้อม copy ทุกขั้นตอนของ Routing ฉบับนั้นเข้ามาเป็น
 * งานของใบนี้ — วิศวกรจะออก revision ใหม่พรุ่งนี้ก็ได้ และใบนี้จะยังผลิตตามสูตรเดิม
 * จนจบ (`BUSINESS_RULES.md` ข้อ 2) ผู้ใช้ที่ไม่เข้าใจข้อนี้จะไม่เข้าใจว่าทำไมใบที่
 * ปล่อยไปแล้วถึงไม่สนใจ revision ใหม่ของเขา คำอธิบายจึงอยู่ **ตรงจุดที่กดปุ่ม**
 *
 * กติกาสามข้อที่ทั้งไฟล์นี้ยืนอยู่บน — ชุดเดียวกับ `ProductTransitionPanel`:
 *
 * 1. **`expected_status` คือสิ่งที่ถูกเรนเดอร์ ไม่ใช่สิ่งที่โค้ดคิดว่าควรเป็น**
 *    ค่าที่ส่งไปถูกอ่านจาก `order.status` ก้อนเดียวกับที่ผู้ใช้กำลังมองอยู่ตอนกดปุ่ม
 * 2. **`409` ไม่ลองใหม่ให้อัตโนมัติ** มันแปลว่ามีคนอื่นขยับใบนี้ไปแล้ว สิ่งที่ทำคือ
 *    ดึงของจริงมาแสดง **หนึ่งครั้ง** แล้วให้คนเป็นผู้ตัดสินใหม่
 * 3. **ความล้มเหลวห้ามขยับสถานะบนจอ** สถานะที่แสดงจะเปลี่ยนก็ต่อเมื่อเซิร์ฟเวอร์
 *    ส่ง resource ใหม่กลับมาเท่านั้น ไม่มีการเดาผลล่วงหน้าแบบ optimistic
 *
 * ⚠️ แถบนี้ถูกเรนเดอร์ทุกสถานะของใบ ไม่ใช่เฉพาะตอนที่สั่งปล่อยได้ — ถ้าหน้าจอแม่
 * ถอดมันออกทันทีที่สถานะขยับ ผลของคำสั่งที่เพิ่งกด (ทั้งสำเร็จและล้มเหลว) จะหายไป
 * พร้อมกับแถบ และผู้ใช้จะไม่มีทางรู้ว่าเกิดอะไรขึ้น แถบจึงเป็นผู้ตัดสินเองว่าจะเสนอ
 * ปุ่มหรือจะอธิบายว่าทำไมไม่มีปุ่ม
 *
 * ⚠️ การเลือกแสดง/ไม่แสดงปุ่มที่นี่เป็น **ความสะดวก ไม่ใช่ด่านความปลอดภัย**
 * เซิร์ฟเวอร์ตรวจทั้งสิทธิ์ สถานะ และ D-010 เองทุกคำขอ
 */

import { useEffect, useRef, useState } from 'react';

import { ProductionOrderFailureNotice } from './ProductionOrderFailureNotice';
import { orderStatusLabel } from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import {
  fetchProductionOrder,
  releaseProductionOrder,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import { RELEASABLE_STATUS, type ProductionOrder } from '../../lib/productionOrders/types';

export interface ProductionOrderReleasePanelProps {
  /** record ที่กำลังถูกแสดงอยู่ — `status` ของก้อนนี้คือ `expected_status` ที่จะส่ง */
  readonly order: ProductionOrder;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** ส่ง record ที่เซิร์ฟเวอร์ยืนยันแล้วกลับให้หน้ารายละเอียดแสดงแทนของเดิม */
  readonly onOrderChanged: (order: ProductionOrder) => void;
}

/** ผลของการดึงข้อมูลใหม่หลัง `409` — `null` คือยังไม่ได้ลอง (ความล้มเหลวแบบอื่น) */
type RefreshOutcome = 'ok' | 'failed';

type PanelState =
  | { readonly status: 'idle' }
  | { readonly status: 'working' }
  | { readonly status: 'done'; readonly resultStatus: string }
  | {
      readonly status: 'failed';
      readonly failure: ProductionOrderFailure;
      readonly refreshed: RefreshOutcome | null;
    };

export function ProductionOrderReleasePanel({
  order,
  apiBaseUrl,
  fetchImpl,
  onOrderChanged,
}: ProductionOrderReleasePanelProps) {
  const [state, setState] = useState<PanelState>({ status: 'idle' });

  /**
   * สั่งปล่อยเดินจากร่างเท่านั้น (ข้อ 14) — และ `CLOSED`/`CANCELLED` ไม่มีทางกลับ
   *
   * อ่านจากสถานะที่กำลังถูกแสดงอยู่ ไม่ใช่จากผลของคำสั่งที่เพิ่งกด
   */
  const releasable = order.status === RELEASABLE_STATUS;

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = async () => {
    if (state.status === 'working') {
      return;
    }

    // ⚠️ อ่านตรงนี้ ตอนกด และจากก้อนที่กำลังถูกแสดงอยู่
    const expectedStatus = order.status;
    setState({ status: 'working' });

    const result = await releaseProductionOrder(
      order.production_order_id,
      expectedStatus,
      { apiBaseUrl, fetchImpl },
    );
    if (!mounted.current || result.status === 'cancelled') {
      return;
    }

    if (result.status === 'ready') {
      setState({ status: 'done', resultStatus: result.order.status });
      onOrderChanged(result.order);
      return;
    }

    // สถานะที่แสดงอยู่ไม่ถูกแตะ — ความล้มเหลวไม่ใช่การเปลี่ยนสถานะ
    setState({ status: 'failed', failure: result, refreshed: null });
    if (result.status !== 'conflict') {
      return;
    }

    // `409` เท่านั้นที่ตามด้วยการอ่านใหม่ และเป็นการ **อ่าน** ไม่ใช่การสั่งซ้ำ
    const refreshed = await fetchProductionOrder(order.production_order_id, {
      apiBaseUrl,
      fetchImpl,
    });
    if (!mounted.current) {
      return;
    }
    if (refreshed.status === 'ready') {
      onOrderChanged(refreshed.order);
      setState({ status: 'failed', failure: result, refreshed: 'ok' });
      return;
    }
    setState({ status: 'failed', failure: result, refreshed: 'failed' });
  };

  return (
    <>
      <section
        className="cap-card"
        aria-label="สั่งปล่อยใบสั่งผลิต"
        data-testid="production-order-release"
      >
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            สั่งปล่อยเข้าสู่การผลิต (D-010){' '}
            <InfoHint
              label="เกี่ยวกับการสั่งปล่อย"
              text="ผู้สั่งปล่อยต้องไม่ใช่ผู้เปิดใบนี้ — เซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอ การแสดงหรือไม่แสดงปุ่มที่นี่เป็นเพียงความสะดวกของหน้าจอ"
            />
          </h2>
        </div>

        {/*
          คำอธิบาย snapshot อยู่เหนือปุ่ม ไม่ใช่ในหน้าช่วยเหลือที่ไม่มีใครเปิด —
          นี่คือสิ่งที่ผู้ใช้กำลังผูกมัดตัวเองไว้กับมันตอนกด
        */}
        {releasable ? (
          <div className="cap-notice" data-testid="production-order-snapshot-warning" role="note">
            <StatusBadge tone="warning">คำสั่งนี้ตรึงสูตรไว้ถาวร</StatusBadge>
            <h3 className="cap-notice__title">
              การสั่งปล่อยจะบันทึกสูตร BOM และขั้นตอน Routing ที่ใช้อยู่ “ณ ตอนนี้” ติดไปกับใบนี้
            </h3>
            <p className="cap-notice__text">
              เซิร์ฟเวอร์จะหาฉบับที่ใช้อยู่จริงของสินค้ากับโรงงานคู่นี้ เขียน{' '}
              <code className="cap-table__code">bom_id</code> และ{' '}
              <code className="cap-table__code">routing_id</code> ลงในใบนี้ แล้วคัดลอกทุกขั้นตอนของ
              Routing ฉบับนั้นมาเป็นงานของใบนี้ — <strong>ทั้งหมดในธุรกรรมเดียว</strong>
            </p>
            <p className="cap-notice__text">
              หลังจากนั้น ถ้าวิศวกรออก revision ใหม่ของ BOM หรือ Routing พรุ่งนี้{' '}
              <strong>ใบนี้จะยังประกอบตามสูตรเดิมจนจบ ไม่เปลี่ยนกลางคัน</strong> นั่นคือสิ่งที่ตั้งใจ
              ไม่ใช่ข้อมูลที่ค้างเก่า (<code className="cap-table__code">BUSINESS_RULES.md</code>{' '}
              ข้อ 2) — ถ้ากำลังจะแก้สูตร ให้แก้ก่อนแล้วค่อยกลับมาสั่งปล่อย
            </p>
            <p className="cap-notice__note">
              ใบนี้ยังไม่มีสูตรผูกอยู่จนกว่าจะสั่งปล่อย — ร่างไม่บังคับว่าต้องมี BOM/Routing
              แต่การสั่งปล่อยบังคับ และจะถูกปฏิเสธถ้ายังไม่มีฉบับที่ใช้อยู่
            </p>
          </div>
        ) : null}

        <div className="cap-card__foot">
          {releasable ? (
            <div className="cap-form__actions">
              <button
                type="button"
                className="cap-button cap-button--primary"
                data-testid="production-order-release-submit"
                disabled={state.status === 'working'}
                onClick={() => {
                  void run();
                }}
              >
                {state.status === 'working'
                  ? 'กำลังสั่งปล่อย…'
                  : `สั่งปล่อยใบนี้ (จาก ${orderStatusLabel(order.status)})`}
              </button>
            </div>
          ) : (
            <p data-testid="production-order-release-unavailable">
              ใบที่สถานะ “{orderStatusLabel(order.status)}” ไม่มีคำสั่งสั่งปล่อยแล้ว —
              การสั่งปล่อยเดินจากร่างเท่านั้น (ข้อ 14) และใบที่พ้นจากร่างไปแล้วไม่มีทางกลับมาเป็นร่าง
              อีก
            </p>
          )}
        </div>
      </section>

      {state.status === 'done' ? (
        <div className="cap-banner" role="status" data-testid="production-order-release-success">
          <strong>สั่งปล่อยแล้ว</strong>
          <span>
            สถานะล่าสุดที่เซิร์ฟเวอร์ยืนยันคือ {orderStatusLabel(state.resultStatus)} (
            {state.resultStatus}) — สูตรที่ถูกตรึงไว้กับใบนี้แสดงอยู่ในหัวข้อ “สูตรที่ถูกตรึงไว้”
            ด้านล่าง
          </span>
        </div>
      ) : null}

      {state.status === 'failed' ? (
        <>
          <ProductionOrderFailureNotice
            failure={state.failure}
            context="release"
            plantCode={order.plant_code ?? undefined}
          />
          {state.refreshed === null ? null : (
            <p className="cap-banner__note" data-testid="production-order-release-refresh">
              {state.refreshed === 'ok'
                ? 'ดึงข้อมูลล่าสุดของใบนี้มาแสดงแทนแล้ว และไม่ได้ส่งคำสั่งเดิมซ้ำ — ตรวจสถานะจริงก่อนตัดสินใจใหม่'
                : 'ดึงข้อมูลล่าสุดไม่สำเร็จ สิ่งที่เห็นบนหน้าจออาจไม่ใช่สถานะจริงแล้ว — เปิดหน้านี้ใหม่ก่อนตัดสินใจ'}
            </p>
          )}
        </>
      ) : null}
    </>
  );
}
